<script>
/**
 * The TeamRegistration view works as the manager panel for the 
 * registration status of the players. 
 *
 * Usage: 
 *  <team-registration 
 *    :teamName="homeTeamLabel"
 *    :starterList="homeStarterList"
 *    :benchList="homeBenchList"
 *    @switch-tab="selectedTeamTab = 1">
 *  </team-registration>
 *  teamName:String - The team name.
 *  starterList:Array - The starter player list.
 *  benchList:Array - The bench player list.
 *  switch-tab:Event - Trigger the event after switched the tab.
 */

//--------------------------------------------------
// imported classes
//--------------------------------------------------
import { QNImageUtil } from '@/utils'
import ObjectFunc from '@/utils/ObjectFunc'
import { getPlayerRosterInfo, updatePlayerRosterInfo, updateOfficerRollCallStatus } from '@/api/match-team-game'

//--------------------------------------------------
// main script
//--------------------------------------------------
export default {
    //--------------------------------------------------
	// component name
	//--------------------------------------------------
	name: 'team-registration',

    //--------------------------------------------------
    // component props
    //--------------------------------------------------
	props: {
        /* The team name */
        teamName: String,
        /* The starter player list */
        starterList: Array,
        /* The bench player list */
        benchList: Array,
        /* The officer player list */
        officerList: Array
	},

    //--------------------------------------------------
    // component emits
    //--------------------------------------------------
	emits: ['switch-tab'],

    //--------------------------------------------------
    // component data
    //--------------------------------------------------
	data() {
		return {
            /* The roster status includes starter(1) and bench(2) */
            rosterStatus: 1,
            /* The selected player from the starter or bench player list */
            selectedPlayer: {},
            /* The selected player list for the starter or bench */
            selectedPlayerList: []
		}
	},

    computed: {
        nonePlayerLabel() {
            if (this.rosterStatus == 1) {
                return '暂未设置首发名单'
            }
            else if (this.rosterStatus == 2) {
                return '暂未设置替补名单'
            }
            else if (this.rosterStatus == 3) {
                return '暂未设置官员名单'
            }
            else {
                return ''
            }
        },
    },
    
    //--------------------------------------------------
    // component methods
    //--------------------------------------------------
    methods: {
        //--------------------------------------------------
        // request method
        //--------------------------------------------------
        /**
         * @request
         * Get the player registration status according to the player id.
         * Use the request service to retrive the player's related information.
         * After the successful response, try to re-render the bindable data.
         * @param player The player object.
         * @return The reponse object.
         */
        getPlayerRegStatus(player) {
            if (this.rosterStatus == 3) {
                this.getOfficerRegStatus(player)
                return
            }
            const routerParams = this.$route.params
            const params = {
                gameId: routerParams.gameId,
                playerId: player.id
            }
            getPlayerRosterInfo(params).then(response => {
                console.log('get player roster info', response.data)
                const data = response.data
                player.avatar = data.player.avatar
                player.regStatus = data.player.rollCallStatus
                player.idCardNo = data.player.IDCardNo
                // set the selected player
                this.selectedPlayer = player
                // trigger the render manually
                // avoid missing the active class of the player item
                this.selectedPlayer.update = Math.random()
            })
        },
        getOfficerRegStatus(player) {
            console.log('get officer reg status', player)
            this.selectedPlayer = player
        },
        /**
         * @request
         * Update the player registration status according to the new status.
         * Use the request service to update the new status.
         * After the successful response, try to re-render the bindable data.
         * @param regStatus The registration status.
         * @return The reponse object.
         */
        updatePlayerRegStatus(regStatus) {
            const routerParams = this.$route.params
            const params = {
                playerId: this.selectedPlayer.id,
                gameId: routerParams.gameId
            }
            const data = {
                status: regStatus
            }
            updatePlayerRosterInfo(params, data).then(response => {
                console.log('update player roster info', response)
                this.selectedPlayer.regStatus = regStatus
                const clonedPlayer = ObjectFunc.cloneObject(this.selectedPlayer)
                // update the selected player
                this.selectedPlayer = clonedPlayer
                // update the item in the selected player list
                for (let i = 0; i < this.selectedPlayerList.length; i++) {
                    let player = this.selectedPlayerList[i]
                    if (player.id == this.selectedPlayer.id) {
                        player.regStatus = this.selectedPlayer.regStatus
                    }
                }
            })
        },
        updateOfficerRegStatus(regStatus) { 
            const routerParams = this.$route.params
            const params = {
                officerId: this.selectedPlayer.id,
                gameId: routerParams.gameId
            }
            const data = {
                status: regStatus
            }
            updateOfficerRollCallStatus(params, data).then(response => {
                console.log('update officer roll call status', response)
                this.selectedPlayer.regStatus = regStatus
            })
        },

        //--------------------------------------------------
        // handler method
        //--------------------------------------------------
        /**
         * @handler
         * Handle the starter button click event.
         */
        onStarterBtnClick() {
            this.rosterStatus = 1;
            this.selectedPlayerList = this.starterList
            // validate the starter list
            if (this.selectedPlayerList.length > 0) {
                // prefetch the first player and click it as selected
                const firstPlayer = this.selectedPlayerList[0]
                this.getPlayerRegStatus(firstPlayer)
            }
            else {
                // clear the selected player for the previous list
                this.selectedPlayer = {}
            }
        },
        /**
         * @handler
         * Handle the bench button click event.
         */
        onBenchBtnClick() {
            this.rosterStatus = 2
            this.selectedPlayerList = this.benchList
            // validate the starter list
            if (this.selectedPlayerList.length > 0) {
                // prefetch the first player and click it as selected
                const firstPlayer = this.selectedPlayerList[0]
                this.getPlayerRegStatus(firstPlayer)
            }
            else {
                // clear the selected player for the previous list
                this.selectedPlayer = {}
            }
        },
        onOfficerBtnClick() {
            this.rosterStatus = 3
            this.selectedPlayerList = this.officerList
            // validate the starter list
            if (this.selectedPlayerList.length > 0) {
                // prefetch the first player and click it as selected
                const firstPlayer = this.selectedPlayerList[0]
                this.getOfficerRegStatus(firstPlayer)
            }
            else {
                // clear the selected player for the previous list
                this.selectedPlayer = {}
            }
        },
        /**
         * @handler
         * Handle the approved button click event.
         */
        onApprovedBtnClick() {
            if (this.rosterStatus == 3) {
                this.updateOfficerRegStatus(1)
            }
            else {
                this.updatePlayerRegStatus(1)
            }
        },
        /**
         * @handler
         * Handle the rejected button click event.
         */
        onRejectedBtnClick() {
            if (this.rosterStatus == 3) {
                this.updateOfficerRegStatus(2)
            }
            else {
                this.updatePlayerRegStatus(2)
            }
        },
        /**
         * @handler
         * Handle the reset button click event.
         */
        onResetBtnClick() {
            if (this.rosterStatus == 3) {
                this.updateOfficerRegStatus(0)
            }
            else {
                this.updatePlayerRegStatus(0)
            }
        },

        //--------------------------------------------------
        // private method
        //--------------------------------------------------
        /**
         * @private
         * Adjust the image url according to the width and height.
         * @param img The identifier string for the image source.
         * @param w The image width number.
         * @param h The image height number.
         * @return The completed full url string.
         */
		adjustImageSize(img, w, h) {
			return QNImageUtil.imageView(img, w, h)
		},
    },

    /**
     * @mounted
     * Set the starter switcher if mounted.
     * Try to get the first player registration status.
     */
    mounted() {
        this.onStarterBtnClick()
    },

    //--------------------------------------------------
    // component watch
    //--------------------------------------------------
    watch: {
        /**
         * Watch the parent attribute to update the child property.
         */
        starterList() {
            this.onStarterBtnClick()
        },
    }
}
</script>

<template>
    <!-- component -->
    <div class="team-registration">
        <!-- head start -->
        <div class="head">
            <div class="team-label truncate">{{ teamName }}</div>
            <div class="switch-btn" @click="$emit('switch-tab')">切换球队<img class="right-arrow" src="@/assets/match-team-game/right-icon.svg"/></div>
        </div>
        <!-- head end -->
        <!-- body start -->
        <div class="body">
            <div class="roster-switcher-wrapper">
                <div class="roster-switcher">
                    <div class="switcher-unit left-side" 
                        :class="{ active: rosterStatus == 1}"
                        @click="onStarterBtnClick">
                        <span class="label">首发</span>
                    </div>
                    <div class="switcher-unit" 
                        :class="{ active: rosterStatus == 2}"
                        @click="onBenchBtnClick">
                        <span class="label">替补</span>
                    </div>
                    <div class="switcher-unit right-side" 
                        :class="{ active: rosterStatus == 3}"
                        @click="onOfficerBtnClick">
                        <span class="label">官员</span>
                    </div>
                </div>
                <div class="player-section">
                    <div v-if="selectedPlayerList.length > 0" class="player-list starter-player-list">
                        <div class="player-item"
                            :class="{ active: player.id == selectedPlayer.id }"
                            v-for="player in selectedPlayerList" 
                            :key="player.id"
                            @click="getPlayerRegStatus(player)">
                            <div class="reg-status-indicator">
                                <img v-if="player.regStatus == 1" src="@/assets/match-team-game/approved-symbol.svg"/>
                                <img v-else-if="player.regStatus == 2" src="@/assets/match-team-game/rejected-symbol.svg"/>
                                <img v-else class="transparent"/>
                            </div>
                            <div class="info-section">
                                <div class="name-section">
                                    <span class="name-label block truncate">{{ player.name }}</span>
                                </div>
                                <div class="attr-label" v-if="rosterStatus == 1 || rosterStatus == 2">
                                    <div class="num-wrapper">
                                        <span class="num-label">{{ player.number }}</span>
                                    </div>
                                    <div class="pos-wrapper" v-if="player.position">
                                        <span class="pos-label">{{ player.position }}</span>
                                    </div>
                                </div>
                                <div class="attr-label" v-if="rosterStatus == 3">
                                     <div class="pos-wrapper" v-if="player.role">
                                        <span class="pos-label">{{ player.role }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="none-player-list">
                        <div class="blank-area">
                            <span class="label">{{ nonePlayerLabel }}</span>
                        </div>
                    </div>
                    <div v-if="selectedPlayer.id" class="player-info">
                        <div class="avatar">
                            <img v-if="selectedPlayer.avatar" :src="adjustImageSize(selectedPlayer.avatar, 310, 414)"/>
                            <img v-else src="@/assets/match-team-game/avatar-lg-symbol.svg"/>
                        </div>
                        <div class="name">
                            <span class="label">{{ selectedPlayer.name }}</span>
                        </div>
                        <div class="identifier">
                            <span class="label truncate block" v-if="selectedPlayer.idCardNo">身份证：{{ selectedPlayer.idCardNo }}</span>
                        </div>
                        <div class="operation-blk reg-status-none" 
                            v-if="selectedPlayer.regStatus == 0">
                            <div class="btn approved-btn" 
                                @click="onApprovedBtnClick">
                                <span class="label">确认检录</span>
                            </div>
                            <div class="btn rejected-btn"
                                @click="onRejectedBtnClick">
                                <span class="label">检录不通过</span>
                            </div>
                        </div>
                        <div class="operation-blk reg-status-approved" 
                            v-else-if="selectedPlayer.regStatus == 1">
                            <div class="btn approved-message">
                                <img src="@/assets/match-team-game/approved-symbol.svg"/>
                                <span class="label">已检录成功</span>
                            </div>
                            <div class="btn reset-btn"
                                @click="onResetBtnClick">
                                <span class="label">重新检录</span>
                            </div>
                        </div>
                        <div class="operation-blk reg-status-rejected" 
                            v-else-if="selectedPlayer.regStatus == 2">
                            <div class="btn rejected-message">
                                <div class="vertical-wrapper">
                                    <img src="@/assets/match-team-game/rejected-symbol.svg"/>
                                </div>
                                <div class="vertical-wrapper">
                                    <span class="label">检录不通过</span>
                                </div>
                            </div>
                            <div class="btn retry-btn"
                                @click="onResetBtnClick">
                                <span class="label">再次检录</span>
                            </div>
                        </div>
                    </div>
                    <div v-else class="none-player-info">
                        <div class="blank-avatar">
                            <img src="@/assets/match-team-game/avatar-lg-symbol.svg"/>
                        </div>
                        <div class="blank-name">
                            <span class="label">未选择队员</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- body end -->
    </div>
</template>

<style scoped>
/**
 * Common
 */
.inline-block {
    display: inline-block;
}
.block {
    display: block;
}
.valign-top {
    vertical-align: top;
}
.truncate {
	word-break: break-all;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.transparent {
    opacity: 0;
}
/**
  * @head
  */
.head {
    margin: 18px 18px 15px 22px;
    position: relative;
}
.team-label {
    display: inline-block;
    font-family: Source Han Sans;
    font-size: 16px;
    font-weight: bold;
    line-height: 16px;
    letter-spacing: normal;
    color: #3D3D3D;
    max-width: 250px;
}
.switch-btn {
    display: inline-block;
    position: absolute;
    right: 18px;
    bottom: 4px;
    margin-right: 2px;
    font-family: Source Han Sans;
    font-size: 14px;
    font-weight: 500;
    line-height: 14px;
    text-align: right;
    letter-spacing: 0.32px;
    color: #0FAC7F;
}
.right-arrow {
    position: absolute;
    top: -1.5px;
}
/**
  * @body
  */
.roster-switcher {
    margin: 15px 20px 19px 20px;
}
.switcher-unit {
    display: inline-block;
    width: 73px;
    height: 32px;
    box-sizing: border-box;
    border: 1px solid #D8D8D8;
    font-family: Source Han Sans;
    font-size: 12px;
    font-weight: normal;
    line-height: normal;
    letter-spacing: normal;
    color: #676767;
}
.left-side {
    border-radius: 4px 0px 0px 4px;
}
.right-side {
    border-radius: 0px 4px 4px 0px;
}
.switcher-unit.active{
    background: #0FAC7F;
    color: #FFFFFF;
}
.switcher-unit .label {
    width: 100%;
    height: 100%;
    display: inline-grid;
    align-items: center;
    text-align: center;
}
.player-item {
    width: 127px;
    height: 53px;
}
.player-item.active {
    background: rgba(15,172,127,.1);
    border-right: 3px solid #0FAC7F;
}
.reg-status-indicator {
    display: inline-block;
    margin-left: 17px;
    bottom: 10px;
    position: relative;
}
.reg-status-indicator img {
    width: 15px;
    height: 15px;
}
.info-section {
    display: inline-block;
    margin-top: 9px;
    margin-left: 9px;
}
.name-section {
}
.name-label {
    font-family: AlibabaPuHuiTi;
    font-size: 15px;
    font-weight: normal;
    line-height: 15px;
    letter-spacing: 0.1em;
    color: #000000;
    max-width: 85px;
}
.attr-label {
    margin-top: 4px;
    position: relative;
}
.num-wrapper {
    display: inline-block;
    width: 15px;
    height: 15px;
    border-radius: 3px;
    background: #F0F0F3;
    box-sizing: border-box;
    border: 0.5px solid #979797;
    margin-right: 2px;
    text-align: center;
}
.num-label {
    font-family: AlibabaPuHuiTi;
    font-size: 12px;
    font-weight: normal;
    line-height: normal;
    letter-spacing: normal;
    color: #4D4D4D;
    bottom: 3px;
    position: relative;
}
.pos-wrapper {
    height: 14px;
    display: inline-block;
    border-radius: 2px;
    background: #F0F0F3;
    border: 0.5px solid #979797;
    text-align: center;
}
.pos-label {
    font-family: AlibabaPuHuiTi;
    font-size: 11px;
    font-weight: normal;
    line-height: normal;
    letter-spacing: normal;
    color: #4D4D4D;
    padding-left: 4px;
    padding-right: 4px;
    bottom: 3px;
    position: relative;
}
.player-list {
    display: inline-block;
    height: calc(100vh - 243px);
    overflow-y: scroll;
    overflow-x: hidden;
    border-right: 1px solid #F0F0F3;
}
.none-player-list {
    display: inline-block;
    height: calc(100vh - 243px);
    width: 127px;
    border-right: 1px solid #F0F0F3;
    text-align: center;
    padding-top: 189px;
}
.none-player-list .blank-area .label {
    font-family: AlibabaPuHuiTi;
    font-size: 13px;
    font-weight: normal;
    line-height: normal;
    letter-spacing: normal;
    color: #8D8D8D;
    width: 52px;
    height: 36px;
    display: block;
    margin-left: auto;
    margin-right: auto;
}
.player-info {
    display: inline-block;
    width: 244px;
    vertical-align: top;
}
.none-player-info {
    display: inline-block;
    width: 244px;
    vertical-align: top;
}
.blank-avatar {
    width: 155px;
    height: 207px;
    border-radius: 6px;
    background: #D9D9D9;
    margin-left: auto;
    margin-right: auto;
}
.blank-avatar img {
    width: 155px;
    height: 207px;
}
.blank-name {
    margin-top: 24px;
    text-align: center;
}
.blank-name .label {
    font-family: AlibabaPuHuiTi;
    font-size: 18px;
    font-weight: normal;
    line-height: normal;
    letter-spacing: normal;
    color: #8D8D8D;
}
.avatar {
    text-align: center;
}
.avatar img {
    border-radius: 6px;
    width: 155px;
    height: 207px;
}
.player-info .name {
    margin-top: 24px;
    text-align: center;
}
.player-info .name .label {
    font-family: AlibabaPuHuiTi;
    font-size: 18px;
    font-weight: bold;
    line-height: normal;
    letter-spacing: normal;
    color: #3D3D3D;
}
.player-info .identifier {
    text-align: center;
}
.player-info .identifier .label {
    font-family: AlibabaPuHuiTi;
    font-size: 14px;
    font-weight: normal;
    line-height: normal;
    letter-spacing: normal;
    color: #3D3D3D;
}
.operation-blk {
    margin-top: 33px;
    margin-bottom: 16px;
}
.btn {
    text-align: center;
    /**
     * avoid the compatiable issue on the app
     * NOT: align-content: center;
     * BUT: line-height: 1;
     */
    line-height: 45px;
    width: 185px;
    height: 45px;
    border-radius: 6px;
    margin-left: auto;
    margin-right: auto;
}
.approved-btn {
    background: #0FAC7F;
}
.approved-btn .label {
    font-family: AlibabaPuHuiTi;
    font-size: 15px;
    font-weight: 500;
    line-height: normal;
    letter-spacing: normal;
    color: #FFFFFF;
}
.rejected-btn {
    margin-top: 12px;
    box-sizing: border-box;
    border: 1px solid #E82424;
}
.rejected-btn .label {
    font-family: AlibabaPuHuiTi;
    font-size: 15px;
    font-weight: 500;
    line-height: normal;
    letter-spacing: normal;
    color: #E82424;
}
.approved-message .label {
    margin-left: 7px;
    font-family: AlibabaPuHuiTi;
    font-size: 15px;
    font-weight: 500;
    line-height: normal;
    letter-spacing: normal;
    color: #0FAC7F;
}
.reset-btn {
    margin-top: 12px;
    width: 121px;
    height: 35px;
    line-height: 35px;
    border: 1px solid #D8D8D8;
}
.reset-btn .label {
    font-family: AlibabaPuHuiTi;
    font-size: 15px;
    font-weight: normal;
    line-height: normal;
    letter-spacing: normal;
    color: #8D8D8D;
}
.rejected-message .label {
    margin-left: 6px;
    font-family: AlibabaPuHuiTi;
    font-size: 15px;
    font-weight: 500;
    line-height: normal;
    letter-spacing: normal;
    color: #E82323;
}
.retry-btn {
    margin-top: 12px;
    border: 1px solid #0FAC7F;
}
.retry-btn .label {
    font-family: AlibabaPuHuiTi;
    font-size: 15px;
    font-weight: 500;
    line-height: normal;
    letter-spacing: normal;
    color: #0FAC7F;
}
.rejected-message img {
    bottom: -4px;
    position: relative;
}
.vertical-wrapper {
    display: inline-block;
    height: 100%;
    align-content: center;
}
</style>